import { Injectable } from '@angular/core';
import { catchError, concatMap, from, fromEvent, map, Observable, of, retry, switchMap, take, tap, timer, toArray } from 'rxjs';
import { ConfigurationService } from './configuration.service';
import { HttpBackend, HttpClient, HttpParams } from '@angular/common/http';
import { AiQuoteChatResponse, AiQuoteInitResponse, Quote } from '../models/ai-quote-chat.models';
import { AuthenticationService } from './authentication.service';
import { EventSourcePolyfill } from 'event-source-polyfill';
import { filter } from 'rxjs/operators';
import { HttpEventType, HttpEvent } from '@angular/common/http';

const RETRY_DELAY_MILLISECONDS = 5000;
const MAX_RETRIES = 10;

@Injectable({
  providedIn: 'root'
})

export class AiQuoteChatService {
  baseUrl: string;
  private conversationId: string = '';
  private quoteId: string = '';
  eventSource: EventSourcePolyfill;
  private readonly rawHttp: HttpClient;

  constructor(
    private readonly http: HttpClient,
    private readonly configurationService: ConfigurationService,
    private readonly authenticationService: AuthenticationService,
    httpBackend: HttpBackend
  ) {
    // Use a client without interceptors for SAS uploads so auth headers are not injected.
    this.rawHttp = new HttpClient(httpBackend);
    this.baseUrl = this.configurationService.getConfig('preConfigUrl');
  }

  initializeSession(): Observable<AiQuoteInitResponse> {
    return this.http.post<AiQuoteInitResponse>(
      `${this.baseUrl}/aiquote/chat/init`,
      null
    ).pipe(
      catchError((err) => this.handleError(err))
    );
  }

  setSessionId(conversationId: string) {
    this.conversationId = conversationId;
  }

  getSessionId(): string {
    return this.conversationId;
  }

  setQuoteId(quoteId: string) {
    this.quoteId = quoteId;
  }

  getQuoteId(): string {
    return this.quoteId;
  }

  endAIChatSession(): Observable<any> {
    const url = `${this.baseUrl}/aiquote/chat/conclude/${this.conversationId}`;
    return this.http.delete(url).pipe(
      catchError((err) => this.handleError(err))
    );
  }

  submitMessageToAI(payload: any): Observable<AiQuoteChatResponse> {
    const url = `${this.baseUrl}/aiquote/chat/continue/${this.conversationId}`;
    return this.http.post<AiQuoteChatResponse>(url, payload).pipe(
      catchError((err) => this.handleError(err))
    );
  }

  private handleError(error: Error): Promise<any> {
    console.error('An error occurred', error);
    return Promise.reject(error);
  }

  get bearerToken() {
    return this.authenticationService.bearerToken;
  }

  private createEventSource(url: string, options: any): EventSourcePolyfill {
    return new EventSourcePolyfill(url, options);
  }

  // Connect to SSE stream for AI Quote chat updates
  connectToAiQuoteStream(conversationId: string): Observable<any> {
    return new Observable<any>(observer => {
      let eventSource: any;
      let reconnectTimer: ReturnType<typeof setTimeout> | undefined;
      let destroyed = false;

      const connect = (resume = false) => {
        if (destroyed || !navigator.onLine) {
          return;
        }

        // Reuse the same EventSource instance while it is OPEN/CONNECTING.
        if (eventSource && eventSource.readyState !== 2) {
          return;
        }

        // If it is CLOSED, create a fresh object.
        eventSource?.close();

        const streamUrl = `${this.baseUrl}/aiquote/chat/stream?conversationId=${conversationId}&resume=${resume}`;
        
        eventSource = this.createEventSource(
          streamUrl,
          {
            headers: {
              Authorization: `Bearer ${this.bearerToken}`,
            },
            heartbeatTimeout: 300000,
          }
        );

        const dispatch = (event: any) => {
          try {
            observer.next(JSON.parse(event.data));
          } catch {
            observer.next(event.data);
          }
        };

        ['STATUS', 'ANSWER', 'STATE UPDATED', 'FINALIZE', 'ERROR', 'KNOWLEDGE_QUERY']
          .forEach(type => eventSource.addEventListener(type, dispatch));

        eventSource.onmessage = dispatch;

        eventSource.onerror = (error: any) => {
          eventSource?.close();

          if (!this.isNetworkError(error)) {
            return;
          }

          if (!navigator.onLine) {
            // online event will reconnect
            return;
          }

          scheduleReconnect();
        };
      };

      const scheduleReconnect = () => {
        if (destroyed || reconnectTimer) {
          return;
        }

        reconnectTimer = setTimeout(() => {
          reconnectTimer = undefined;
          connect(true);
        }, RETRY_DELAY_MILLISECONDS);
      };

      const onOnline = () => {
        console.log('Network restored. Reconnecting SSE...');
        scheduleReconnect();
      };

      const onOffline = () => {
        console.log('Network disconnected.');
        eventSource?.close();

        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
          reconnectTimer = undefined;
        }
      };

      window.addEventListener('online', onOnline);
      window.addEventListener('offline', onOffline);

      connect();

      return () => {
        destroyed = true;

        eventSource?.close();

        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }

        window.removeEventListener('online', onOnline);
        window.removeEventListener('offline', onOffline);
      };
    });
  }

  /**
   * Returns true when an SSE error appears to be network-related.
   * EventSourcePolyfill may expose details on either top-level fields
   * or on an `error` payload, so check both shapes.
   */
  private isNetworkError(error: any): boolean {
    if (!navigator.onLine) {
      return true;
    }

    const status = error?.status ?? error?.error?.status;

    if (status === 0) {
      return true;
    }

    const message = `${error?.message ?? error?.error?.message ?? ''}`.toLowerCase();

    return /network|offline|failed to fetch|load failed|connection|timeout/.test(message);
  }

  // Finalize the quote and return the quote details
  finalizeQuote(conversationId: string): Observable<Quote> {
    const url = `${this.baseUrl}/aiquote/chat/finalize/${conversationId}`;

    return this.http.post<Quote>(url, {}).pipe(
      catchError(err => this.handleError(err))
    );
  }

  // Upload files for the AI quote and track progress
  uploadAiQuoteFiles(payload: { userMessage: string; quoteId?: string; images: File[] }, uploadProgress?: (completed: number, total: number, stage?: string, urls?: string[], fileIndex?: number, filePercent?: number) => void): Observable<any> {
    const uploadUrlPayload = payload.images.map((file: any) => file.name);
    const uploadUrlParams = new HttpParams().set('quoteId', payload.quoteId || '');

    return this.http.post(`${this.baseUrl}/aiquote/image/uploadUrl`, uploadUrlPayload, {
      params: uploadUrlParams
    }).pipe(
      switchMap((uploadResponse: any) => {
        uploadProgress?.(0, payload.images.length, 'upload-url-complete');
        const fileUploadUrls = Array.isArray(uploadResponse?.fileUploadUrls) ? uploadResponse.fileUploadUrls : [];
        const uploads = this.buildAiQuoteFileUploads(payload.images, fileUploadUrls);

        if (!uploads.length) {
          return of(uploadResponse);
        }

        let completedUploads = 0;

        return from(uploads).pipe(
          concatMap(({ file, uploadUrl }, fileIndex) =>
            this.rawHttp.put(uploadUrl, file, {
              headers: {
                'x-ms-blob-type': 'BlockBlob',
                'Content-Type': file.type || 'application/octet-stream'
              },
              reportProgress: true,
              observe: 'events'
            }).pipe(
              tap((event: HttpEvent<any>) => {
                if (event.type === HttpEventType.UploadProgress && event.total) {
                  const filePercent = Math.round((event.loaded / event.total) * 100);
                  uploadProgress?.(completedUploads, uploads.length, 'file-progress', undefined, fileIndex, filePercent);
                } else if (event.type === HttpEventType.Response) {
                  completedUploads += 1;
                }
              }),
              filter((event: HttpEvent<any>) => event.type === HttpEventType.Response)
            )
          ),
          toArray(),
          map(() => {
            uploadProgress?.(
              uploads.length,
              uploads.length,
              'image-put-complete',
              uploads.map(({ uploadUrl }) => this.stripQueryParams(uploadUrl))
            );
            return uploadResponse;
          })
        );
      }),
      switchMap((uploadResponse: any) => {
        const publicFileUrls = Array.isArray(uploadResponse?.fileUploadUrls)
          ? uploadResponse.fileUploadUrls.map((url: string) => this.stripQueryParams(url))
          : [];

        const requestBody = {
          id: uploadResponse?.potentialCommentId,
          comment: payload.userMessage ?? '',
          quoteId: payload.quoteId,
          imageUrls: publicFileUrls
        };

        return this.http.post(`${this.baseUrl}/aiquote/comment`, requestBody).pipe(catchError(this.handleError));
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Remove query string and return the path portion of a URL.
   * - If `url` is not a string, returns an empty string.
   * - Otherwise returns the substring before the first `?` character.
   */
  private stripQueryParams(url: string): string {
    return typeof url === 'string' ? url.split('?')[0] : '';
  }

  /**
 * Pair local `File` objects with their corresponding upload URLs returned
 * by the server for AI-quote image uploads.
 * - Builds a map from file name -> upload URL by extracting the file name
 *   from each `fileUploadUrls` entry (query params are stripped).
 * - Returns an array of `{ file, uploadUrl }` where the `file.name` matches
 *   a mapped upload URL. Any files without a matching URL are filtered out.
 */
  private buildAiQuoteFileUploads(files: File[], fileUploadUrls: string[]): Array<{ file: File; uploadUrl: string }> {
    if (!files?.length || !fileUploadUrls?.length) {
      return [];
    }

    const urlByFileName = new Map<string, string>();

    fileUploadUrls.forEach((uploadUrl) => {
      const fileName = this.getFileNameFromUploadUrl(uploadUrl);

      if (fileName) {
        urlByFileName.set(fileName, uploadUrl);
      }
    });

    return files
      .map((file) => ({
        file,
        uploadUrl: urlByFileName.get(file.name) || ''
      }))
      .filter((upload) => !!upload.uploadUrl);
  }

  /**
 * Extract the file name from an upload URL.
 * - Strips query parameters, then returns the last path segment.
 * - Attempts `decodeURIComponent` on the extracted name and falls
 *   back to the raw segment if decoding fails.
 * - Returns an empty string for falsy `uploadUrl` input.
 */
  private getFileNameFromUploadUrl(uploadUrl: string): string {
    if (!uploadUrl) {
      return '';
    }

    const normalizedUrl = this.stripQueryParams(uploadUrl);
    const fileName = normalizedUrl.split('/').pop() || '';

    try {
      return decodeURIComponent(fileName);
    } catch {
      return fileName;
    }
  }
}
